import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createArea, createRegion, deleteArea, deleteRegion, getAreas, getRegions, updateArea, updateRegion,
} from '@/api/locations';
import { useAuth } from '@/auth/useAuth';
import { ApiError } from '@/api/client';
import { CodeBoard } from './CodeBoard';

const inputCls = 'rounded border border-border bg-white px-2 py-1.5 text-sm';

export function LocationHierarchyPage() {
  const { isAdmin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'codes' | 'regions'>('codes');

  return (
    <section className="max-w-6xl space-y-6">
      <header>
        <Link to="/locations" className="text-xs text-navy hover:underline">← Locations</Link>
        <h1 className="font-heading text-2xl font-bold text-navy">Regions, areas &amp; reporting branches</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          The reporting hierarchy: a Region contains Areas, and each reporting branch (a Baseplan branch, minor code or debtors division)
          sits in one Area. New IDs are assigned automatically.
        </p>
      </header>
      <div className="inline-flex rounded border border-border bg-white p-0.5 text-sm" role="tablist" aria-label="Hierarchy">
        {([['codes', 'Reporting branches'], ['regions', 'Regions & areas']] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={tab === id ? 'rounded bg-navy px-3 py-1 font-medium text-white' : 'rounded px-3 py-1 font-medium text-darkGrey/70 hover:text-navy'}
          >
            {label}
          </button>
        ))}
      </div>
      {!isAdmin && <p className="text-sm text-darkGrey/70">You need the admin role to change the hierarchy.</p>}
      {tab === 'codes' && <CodeBoard isAdmin={isAdmin} />}
      {tab === 'regions' && (
        <div className="max-w-4xl space-y-8">
          {error && <div role="alert" className="rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red">{error}</div>}
          <RegionsSection isAdmin={isAdmin} onError={setError} />
          <AreasSection isAdmin={isAdmin} onError={setError} />
        </div>
      )}
    </section>
  );
}

function RegionsSection({ isAdmin, onError }: { isAdmin: boolean; onError: (m: string | null) => void }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['locations', 'regions'], queryFn: ({ signal }) => getRegions(signal) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['locations'] });
  const [desc, setDesc] = useState('');
  const [sort, setSort] = useState('');

  const mCreate = useMutation({
    mutationFn: () => createRegion({ RegionDesc: desc.trim(), RegionSort: sort.trim() || null }),
    onSuccess: () => { invalidate(); setDesc(''); setSort(''); onError(null); },
    onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not add the region.'),
  });
  const mDelete = useMutation({ mutationFn: (id: number) => deleteRegion(id), onSuccess: invalidate, onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not delete the region.') });
  const mRename = useMutation({ mutationFn: (v: { id: number; RegionDesc: string }) => updateRegion(v.id, { RegionDesc: v.RegionDesc }), onSuccess: invalidate, onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not rename the region.') });

  return (
    <div>
      <h2 className="mb-2 font-heading text-base font-semibold text-navy">Regions</h2>
      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr><th className="px-3 py-2">ID</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Sort</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {query.data?.map((r) => (
              <tr key={r.RegionId} className="border-t border-border">
                <td className="px-3 py-2 text-darkGrey/70">{r.RegionId}</td>
                <td className="px-3 py-2">
                  {isAdmin
                    ? <EditableText value={r.RegionDesc} onSave={(v) => mRename.mutate({ id: r.RegionId, RegionDesc: v })} />
                    : r.RegionDesc}
                </td>
                <td className="px-3 py-2 text-darkGrey/80">{r.RegionSort ?? '—'}</td>
                <td className="px-3 py-2 text-right">{isAdmin && <button type="button" onClick={() => mDelete.mutate(r.RegionId)} className="text-xs font-semibold text-red hover:underline">Delete</button>}</td>
              </tr>
            ))}
            {query.data?.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-darkGrey/60">No regions yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {isAdmin && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input aria-label="New region description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Region description" className={inputCls} />
          <input aria-label="New region sort" value={sort} onChange={(e) => setSort(e.target.value)} placeholder="Sort" className={`${inputCls} w-20`} />
          <button type="button" disabled={!desc.trim() || mCreate.isPending} onClick={() => mCreate.mutate()} className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">Add region</button>
        </div>
      )}
    </div>
  );
}

function AreasSection({ isAdmin, onError }: { isAdmin: boolean; onError: (m: string | null) => void }) {
  const queryClient = useQueryClient();
  const areasQuery = useQuery({ queryKey: ['locations', 'areas'], queryFn: ({ signal }) => getAreas(undefined, signal) });
  const regionsQuery = useQuery({ queryKey: ['locations', 'regions'], queryFn: ({ signal }) => getRegions(signal) });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['locations'] });
  const regionName = new Map((regionsQuery.data ?? []).map((r) => [r.RegionId, r.RegionDesc] as const));
  const [desc, setDesc] = useState('');
  const [region, setRegion] = useState('');
  const [sort, setSort] = useState('');

  const mCreate = useMutation({
    mutationFn: () => createArea({ AreaDesc: desc.trim(), RegionId: region ? Number(region) : null, AreaSort: sort.trim() || null }),
    onSuccess: () => { invalidate(); setDesc(''); setRegion(''); setSort(''); onError(null); },
    onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not add the area.'),
  });
  const mDelete = useMutation({ mutationFn: (id: number) => deleteArea(id), onSuccess: invalidate, onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not delete the area.') });
  const mRename = useMutation({ mutationFn: (v: { id: number; AreaDesc: string }) => updateArea(v.id, { AreaDesc: v.AreaDesc }), onSuccess: invalidate, onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not rename the area.') });
  const mReassign = useMutation({ mutationFn: (v: { id: number; RegionId: number | null }) => updateArea(v.id, { RegionId: v.RegionId }), onSuccess: invalidate, onError: (e) => onError(e instanceof ApiError ? e.message : 'Could not reassign the area.') });

  return (
    <div>
      <h2 className="mb-2 font-heading text-base font-semibold text-navy">Areas</h2>
      <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
            <tr><th className="px-3 py-2">ID</th><th className="px-3 py-2">Description</th><th className="px-3 py-2">Region</th><th className="px-3 py-2">Sort</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {areasQuery.data?.map((a) => (
              <tr key={a.AreaId} className="border-t border-border">
                <td className="px-3 py-2 text-darkGrey/70">{a.AreaId}</td>
                <td className="px-3 py-2">
                  {isAdmin
                    ? <EditableText value={a.AreaDesc} onSave={(v) => mRename.mutate({ id: a.AreaId, AreaDesc: v })} />
                    : a.AreaDesc}
                </td>
                <td className="px-3 py-2 text-darkGrey/80">
                  {isAdmin ? (
                    <select
                      aria-label={`Region for ${a.AreaDesc}`}
                      value={a.RegionId ?? ''}
                      onChange={(e) => mReassign.mutate({ id: a.AreaId, RegionId: e.target.value ? Number(e.target.value) : null })}
                      className="rounded border border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-border focus:border-border focus:outline-none"
                    >
                      <option value="">—</option>
                      {(regionsQuery.data ?? []).map((r) => <option key={r.RegionId} value={r.RegionId}>{r.RegionDesc}</option>)}
                    </select>
                  ) : (a.RegionId != null ? (regionName.get(a.RegionId) ?? `#${a.RegionId}`) : '—')}
                </td>
                <td className="px-3 py-2 text-darkGrey/80">{a.AreaSort ?? '—'}</td>
                <td className="px-3 py-2 text-right">{isAdmin && <button type="button" onClick={() => mDelete.mutate(a.AreaId)} className="text-xs font-semibold text-red hover:underline">Delete</button>}</td>
              </tr>
            ))}
            {areasQuery.data?.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-darkGrey/60">No areas yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {isAdmin && (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input aria-label="New area description" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Area description" className={inputCls} />
          <select aria-label="New area region" value={region} onChange={(e) => setRegion(e.target.value)} className={inputCls}>
            <option value="">— Region —</option>
            {(regionsQuery.data ?? []).map((r) => <option key={r.RegionId} value={r.RegionId}>{r.RegionDesc}</option>)}
          </select>
          <input aria-label="New area sort" value={sort} onChange={(e) => setSort(e.target.value)} placeholder="Sort" className={`${inputCls} w-20`} />
          <button type="button" disabled={!desc.trim() || mCreate.isPending} onClick={() => mCreate.mutate()} className="rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-40">Add area</button>
        </div>
      )}
    </div>
  );
}

/** Inline-editable text cell — saves on blur if changed. */
function EditableText({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { const t = v.trim(); if (t && t !== value) onSave(t); else if (!t) setV(value); }}
      className="w-48 rounded border border-transparent bg-transparent px-1 py-0.5 text-sm hover:border-border focus:border-border focus:outline-none"
    />
  );
}
